import React from 'react'

import { booleanFilter } from '../../utils/helpers'

/**
 * Types
 */

type SchemaMarkupData = Queries.SchemaMarkupVideoFragment | Queries.SchemaMarkupFAQFragment | null

type SchemaMarkupProps = { data?: readonly SchemaMarkupData[] | null }

/**
 * Schema Parsers
 */

const toVideoSchema = (data: Queries.SchemaMarkupVideoFragment) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    embedUrl: data.embed_url,
    name: data.title,
    description: data.video_description,
    thumbnailUrl:
      data.video_thumbnails.length > 1
        ? data.video_thumbnails.map((thumbnail) => thumbnail?.url)
        : data.video_thumbnails[0]?.url,
    uploadDate: new Date(data.uploaded_date).toISOString(),
    duration: `PT${data.duration_in_secs || 1}S`,
  }
}

const toFAQSchema = (data: Queries.SchemaMarkupFAQFragment) => {
  const validQuestions = data.questions
    ?.filter(booleanFilter)
    .filter(({ answer }) => booleanFilter(answer))

  if (!validQuestions || !validQuestions.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validQuestions.map(({ answer, title }) => ({
      '@type': 'Question',
      name: title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer ?? '',
      },
    })),
  }
}

/**
 * Schema Generator
 */

const toSchema = (data: SchemaMarkupData) => {
  if (!data) return null

  return data.__typename === 'Contentstack_metadata__schema_markup__video'
    ? toVideoSchema(data)
    : toFAQSchema(data)
}

const sanitizeSchemas = (schemas: ReturnType<typeof toSchema>[]) => {
  if (!schemas.length) return null
  if (schemas.length === 1) return schemas[0]

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: schemas,
  }
}

/**
 * Component
 */

export const SchemaMarkup = ({ data }: SchemaMarkupProps) => {
  if (!data || !data.length) return null

  const schemas = data.map(toSchema).filter(Boolean)
  const validSchema = sanitizeSchemas(schemas)

  return <script type="application/ld+json">{JSON.stringify(validSchema, null, 2)}</script>
}
